import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { Plus, Search, Star, X, BookOpen, ChevronLeft, ChevronRight, Layers, Users, CheckCircle2 } from 'lucide-react'
import { RootState } from '../../support/store'
import { CATEGORIES, Series } from './types'
import { useCreateSeries, useSeriesList } from './hooks'
import { categoryAccent } from './utils'

function timeAgo(dateStr?: string): string | null {
  if (!dateStr) return null
  const date = new Date(dateStr)
  if (Number.isNaN(date.getTime())) return null
  const diffDays = Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24))
  if (diffDays <= 0) return 'Updated today'
  if (diffDays === 1) return 'Updated yesterday'
  if (diffDays < 7) return `Updated ${diffDays}d ago`
  if (diffDays < 30) return `Updated ${Math.floor(diffDays / 7)}w ago`
  if (diffDays < 365) return `Updated ${Math.floor(diffDays / 30)}mo ago`
  return `Updated ${Math.floor(diffDays / 365)}y ago`
}

type SortBy = 'newest' | 'oldest' | 'title_asc' | 'title_desc' | 'rating'

const PAGE_SIZE = 12
// Backend caps `limit` at 100; filtering and paging happen client-side.
const FETCH_LIMIT = 100

export default function CyberBytesList() {
  const navigate = useNavigate()
  const currentEmail = useSelector((state: RootState) => state.user.userData?.email) || null
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'all' | 'published' | 'drafts'>('all')
  const [authorFilter, setAuthorFilter] = useState('')
  const [sortBy, setSortBy] = useState<SortBy>('newest')
  const [page, setPage] = useState(1)
  const [showCreateModal, setShowCreateModal] = useState(false)

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 300)
    return () => clearTimeout(handle)
  }, [searchQuery])

  const listQuery = useSeriesList({ search: debouncedSearch, category: selectedCategory, limit: FETCH_LIMIT })
  const series: Series[] = useMemo(() => listQuery.data?.series || [], [listQuery.data])
  const loading = listQuery.isLoading
  const error = listQuery.isError
    ? (listQuery.error as Error)?.message || 'Failed to load series. Please try again.'
    : null

  // No authors endpoint, so derive options from the loaded rows.
  const authorOptions = useMemo(() => {
    const map = new Map<string, string>()
    series.forEach((s) => {
      if (s.created_by?.email) map.set(s.created_by.email, s.created_by.display_name || s.created_by.email)
    })
    return Array.from(map.entries())
      .map(([email, name]) => ({ email, name }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [series])

  const visibleSeries = useMemo(() => {
    let list = series.filter((s) => {
      if (activeTab === 'published') return s.published
      if (activeTab === 'drafts') return !s.published && currentEmail && s.created_by?.email === currentEmail
      return true
    })
    if (authorFilter) list = list.filter((s) => s.created_by?.email === authorFilter)

    const sorted = [...list]
    switch (sortBy) {
      case 'oldest':
        sorted.sort((a, b) => new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime())
        break
      case 'title_asc':
        sorted.sort((a, b) => a.title.localeCompare(b.title))
        break
      case 'title_desc':
        sorted.sort((a, b) => b.title.localeCompare(a.title))
        break
      case 'rating':
        sorted.sort((a, b) => (b.average_rating || 0) - (a.average_rating || 0))
        break
      case 'newest':
      default:
        sorted.sort(
          (a, b) => new Date(b.updated_at || b.created_at || 0).getTime() - new Date(a.updated_at || a.created_at || 0).getTime()
        )
    }
    return sorted
  }, [series, activeTab, authorFilter, sortBy, currentEmail])

  useEffect(() => {
    setPage(1)
  }, [debouncedSearch, selectedCategory, activeTab, authorFilter, sortBy])

  const totalPages = Math.max(1, Math.ceil(visibleSeries.length / PAGE_SIZE))
  const pagedSeries = visibleSeries.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  const handleSelectSeries = (seriesId: number) => {
    navigate(`/cyber-bytes/${seriesId}`)
  }

  return (
    <div className="cb-scope min-h-screen bg-[#f9fafb]">
      <div className="sticky top-0 z-40 bg-white border-b border-[#e5e7eb]">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-[#111827]">Cyber Bytes</h1>
              <p className="text-[#6b7280] text-sm mt-1">Curated learning series combining multiple sources</p>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-[#2563eb] hover:bg-[#1d4ed8] text-white rounded-lg font-medium text-sm transition"
            >
              <Plus className="w-4 h-4" /> Create Series
            </button>
          </div>

          <div className="flex gap-3 items-center flex-wrap">
            <div className="relative flex-1 min-w-64">
              <Search className="absolute left-3 top-3 w-4 h-4 text-[#9ca3af]" />
              <input
                type="text"
                placeholder="Search series..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-[#e5e7eb] rounded-lg bg-white focus:outline-none focus:border-[#049fd9] focus:ring-2 focus:ring-[#049fd9]/10 text-sm transition"
              />
            </div>
            <select
              value={selectedCategory || ''}
              onChange={(e) => setSelectedCategory(e.target.value || null)}
              className="px-3 py-2 border border-[#e5e7eb] rounded-lg bg-white focus:outline-none focus:border-[#049fd9] text-sm"
            >
              <option value="">All Categories</option>
              {CATEGORIES.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.label}
                </option>
              ))}
            </select>
            <select
              value={authorFilter}
              onChange={(e) => setAuthorFilter(e.target.value)}
              className="px-3 py-2 border border-[#e5e7eb] rounded-lg bg-white focus:outline-none focus:border-[#049fd9] text-sm"
            >
              <option value="">All Authors</option>
              {authorOptions.map((a) => (
                <option key={a.email} value={a.email}>
                  {a.name}
                </option>
              ))}
            </select>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortBy)}
              className="px-3 py-2 border border-[#e5e7eb] rounded-lg bg-white focus:outline-none focus:border-[#049fd9] text-sm"
            >
              <option value="newest">Newest first</option>
              <option value="oldest">Oldest first</option>
              <option value="title_asc">Title A–Z</option>
              <option value="title_desc">Title Z–A</option>
              <option value="rating">Highest rated</option>
            </select>
          </div>

          <div className="flex gap-1 mt-4 border-b border-[#e5e7eb]">
            {(['all', 'published', 'drafts'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`relative px-3 py-2.5 text-sm font-medium transition ${
                  activeTab === tab ? 'text-[#111827]' : 'text-[#6b7280] hover:text-[#374151]'
                }`}
              >
                {tab === 'all' ? 'All' : tab === 'published' ? 'Published' : 'My Drafts'}
                {activeTab === tab && <span className="absolute left-0 right-0 -bottom-px h-0.5 rounded-full bg-[#2563eb]" />}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {error && (
          <div className="mb-6 p-4 bg-[#fef2f2] border border-[#fecaca] rounded-lg text-[#b91c1c] text-sm">
            {error}
          </div>
        )}

        {!loading && !error && visibleSeries.length > 0 && (
          <p className="text-xs text-[#9ca3af] mb-4">
            Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, visibleSeries.length)} of {visibleSeries.length} series
          </p>
        )}

        <SeriesListView series={pagedSeries} loading={loading} onSelectSeries={handleSelectSeries} isEmpty={visibleSeries.length === 0} />

        {!loading && totalPages > 1 && (
          <div className="flex items-center justify-center gap-3 mt-8">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="flex items-center gap-1 px-3 py-2 border border-[#e5e7eb] rounded-lg text-sm text-[#374151] disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#f9fafb] transition"
            >
              <ChevronLeft className="w-4 h-4" /> Previous
            </button>
            <span className="text-sm text-[#6b7280]">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="flex items-center gap-1 px-3 py-2 border border-[#e5e7eb] rounded-lg text-sm text-[#374151] disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#f9fafb] transition"
            >
              Next <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {showCreateModal && (
        <CreateSeriesModal
          onClose={() => setShowCreateModal(false)}
          onCreated={(newId) => {
            setShowCreateModal(false)
            if (newId !== undefined) navigate(`/cyber-bytes/${newId}`)
          }}
        />
      )}
    </div>
  )
}

function SeriesListView({
  series,
  loading,
  onSelectSeries,
  isEmpty,
}: {
  series: Series[]
  loading: boolean
  onSelectSeries: (id: number) => void
  isEmpty: boolean
}) {
  if (loading) {
    return (
      <div className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="bg-white rounded-xl border border-[#e5e7eb] overflow-hidden animate-pulse">
            <div className="h-28 bg-[#e5e7eb]" />
            <div className="p-4 space-y-3">
              <div className="h-4 w-3/4 bg-[#e5e7eb] rounded" />
              <div className="h-3 w-full bg-[#f3f4f6] rounded" />
              <div className="h-3 w-2/3 bg-[#f3f4f6] rounded" />
              <div className="h-5 w-20 bg-[#f3f4f6] rounded-full" />
            </div>
          </div>
        ))}
      </div>
    )
  }

  if (isEmpty) {
    return (
      <div className="text-center py-16">
        <div className="w-14 h-14 rounded-full bg-[#f3f4f6] flex items-center justify-center mx-auto mb-4">
          <BookOpen className="w-6 h-6 text-[#9ca3af]" />
        </div>
        <div className="text-[#374151] text-sm font-medium mb-1">No series found</div>
        <p className="text-[#9ca3af] text-xs">Try adjusting your search or filters, or create your first series</p>
      </div>
    )
  }

  return (
    <div className="grid gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {series.map((s) => (
        <SeriesCard key={s.id} series={s} onSelect={onSelectSeries} />
      ))}
    </div>
  )
}

function SeriesCard({ series: s, onSelect }: { series: Series; onSelect: (id: number) => void }) {
  const accent = categoryAccent(s.category)
  const updated = timeAgo(s.updated_at || s.created_at)

  return (
    <div
      onClick={() => onSelect(s.id)}
      className="h-full bg-white rounded-lg border border-[#e5e7eb] hover:border-[#d1d5db] hover:shadow-md transition-all cursor-pointer p-4 flex flex-col"
    >
      <div className="flex items-start justify-end mb-3">
        <span
          className={`px-2 py-0.5 text-[10px] font-semibold uppercase rounded-full border ${
            s.published ? 'bg-[#ecfdf5] text-[#047857] border-[#a7f3d0]' : 'bg-[#fffbeb] text-[#b45309] border-[#fde68a]'
          }`}
        >
          {s.published ? 'Published' : 'Draft'}
        </span>
      </div>

      <h3 className="font-semibold text-[#111827] line-clamp-1 mb-1 text-sm">{s.title}</h3>
      <p className="text-[#6b7280] text-xs line-clamp-2 mb-3 leading-relaxed min-h-[2rem]">{s.description}</p>

      <div className="flex items-center gap-3 flex-wrap text-[11px] text-[#6b7280] mb-3 pb-3 border-b border-[#f3f4f6]">
        <span className="flex items-center gap-1">
          <Layers className="w-3 h-3" /> {s.modules_count ?? 0} {s.modules_count === 1 ? 'episode' : 'episodes'}
        </span>
        <span className="flex items-center gap-1">
          <Users className="w-3 h-3" /> {s.viewers_count ?? 0}
        </span>
        {(s.completed_count || 0) > 0 && (
          <span className="flex items-center gap-1 text-[#059669]">
            <CheckCircle2 className="w-3 h-3" /> {s.completed_count}
          </span>
        )}
        {(s.ratings_count || 0) > 0 && (
          <span className="flex items-center gap-1">
            <Star className="w-3 h-3 fill-[#facc15] text-[#facc15]" />
            {s.average_rating.toFixed(1)} ({s.ratings_count})
          </span>
        )}
      </div>

      <div className="flex items-center justify-between gap-2">
        {s.created_by?.display_name && (
          <div className="flex items-center gap-1.5 min-w-0">
            <div className="w-5 h-5 rounded-full bg-[#f3f4f6] flex items-center justify-center text-[9px] font-bold text-[#6b7280] flex-shrink-0">
              {s.created_by.display_name.charAt(0).toUpperCase()}
            </div>
            <span className="text-[11px] text-[#6b7280] truncate">{s.created_by.display_name}</span>
          </div>
        )}
        <span className="text-[10px] font-semibold whitespace-nowrap" style={{ color: accent }}>
          {CATEGORIES.find((c) => c.id === s.category)?.label || s.category}
        </span>
      </div>

      {(s.tags?.length || 0) > 0 && (
        <div className="flex gap-1 flex-wrap mt-3">
          {s.tags.slice(0, 3).map((tag) => (
            <span key={tag} className="px-2 py-0.5 bg-[#f9fafb] text-[#6b7280] text-[10px] font-medium rounded border border-[#f3f4f6]">
              {tag}
            </span>
          ))}
        </div>
      )}

      {updated && <p className="text-[10px] text-[#9ca3af] mt-auto pt-3 border-t border-[#f3f4f6]">{updated}</p>}
    </div>
  )
}

function CreateSeriesModal({ onClose, onCreated }: { onClose: () => void; onCreated: (newId?: number) => void }) {
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [category, setCategory] = useState('')
  const [tags, setTags] = useState('')
  const [error, setError] = useState<string | null>(null)
  const createMutation = useCreateSeries()
  const loading = createMutation.isPending

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      setError(null)
      const data = await createMutation.mutateAsync({
        title,
        description,
        category,
        tags: tags.split(',').map((t) => t.trim()).filter(Boolean),
      })
      onCreated(data?.id)
    } catch (err: any) {
      console.error('Failed to create series:', err)
      setError(err.message || 'Failed to create series. Please try again.')
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md mx-4 p-6 border border-[#e5e7eb]">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-semibold text-[#111827]">Create Series</h2>
          <button onClick={onClose} className="p-1 hover:bg-[#f3f4f6] rounded-lg transition">
            <X className="w-4 h-4 text-[#9ca3af]" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="p-3 bg-[#fef2f2] border border-[#fecaca] rounded-lg text-[#b91c1c] text-xs">{error}</div>
          )}
          <div>
            <label className="block text-xs font-medium text-[#374151] mb-1.5">Title</label>
            <input
              type="text"
              required
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Series title"
              className="w-full px-3 py-2 border border-[#e5e7eb] rounded-lg focus:outline-none focus:border-[#3b82f6] text-sm"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-[#374151] mb-1.5">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe your series"
              rows={3}
              className="w-full px-3 py-2 border border-[#e5e7eb] rounded-lg focus:outline-none focus:border-[#3b82f6] text-sm resize-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-[#374151] mb-1.5">Category</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full px-3 py-2 border border-[#e5e7eb] rounded-lg focus:outline-none focus:border-[#3b82f6] text-sm"
              >
                <option value="">Select category</option>
                {CATEGORIES.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-[#374151] mb-1.5">Tags</label>
              <input
                type="text"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                placeholder="tag1, tag2"
                className="w-full px-3 py-2 border border-[#e5e7eb] rounded-lg focus:outline-none focus:border-[#3b82f6] text-sm"
              />
            </div>
          </div>

          <div className="flex gap-2 pt-3">
            <button type="button" onClick={onClose} className="flex-1 px-3 py-2 border border-[#e5e7eb] text-[#374151] rounded-lg font-medium text-sm hover:bg-[#f9fafb] transition">
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !title}
              className="flex-1 px-3 py-2 bg-[#2563eb] hover:bg-[#1d4ed8] text-white rounded-lg font-medium text-sm disabled:opacity-50 transition"
            >
              {loading ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

